import React, { useState } from "react";
import { GitCompare, Loader2 } from "lucide-react";
import { compareAds, fmtCtr } from "@/lib/api";
import { useAdStrings } from "@/lib/adStrings";

const label = (a) => `${a.title || a.preview?.name || a.target_id} · ${(a.pincodes || [a.pincode]).length} PIN · ${a.status}`;
const fmtD = (d, l) => (d ? new Date(d).toLocaleDateString(l) : null);

function Metric({ k, v, testid }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1 border-t border-slate-100 text-xs">
      <span className="text-slate-500">{k}</span><b className="text-slate-800 text-right" data-testid={testid}>{v}</b>
    </div>
  );
}

function CompareCard({ c, S, l }) {
  const nd = <span className="text-slate-400 italic font-normal">{S.noData}</span>;
  const ctr = c.ctr === null || c.ctr === undefined ? S.na : fmtCtr(c.ctr);
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-2.5 min-w-0" data-testid={`compare-card-${c.id}`}>
      <div className="text-xs font-semibold truncate" title={c.name}>{c.name}</div>
      <div className="text-[10px] text-slate-500 truncate">{S.type[c.target_type] || c.target_type} · {c.pins} {S.pin} · {S.status[c.status] || c.status}</div>
      <div className="text-[10px] text-slate-400 mb-1">{c.period_from ? `${fmtD(c.period_from, l)} – ${fmtD(c.period_to, l)}` : S.notStarted}</div>
      <Metric k={S.impressions} v={c.has_data ? c.impressions : nd} testid={`compare-impr-${c.id}`} />
      <Metric k={S.clicks} v={c.has_data ? c.clicks : nd} testid={`compare-clicks-${c.id}`} />
      <Metric k={S.ctr} v={c.has_data ? ctr : nd} testid={`compare-ctr-${c.id}`} />
      <Metric k={S.spend} v={c.spend === null || c.spend === undefined ? <span className="text-slate-400 italic font-normal">{S.notAvail}</span> : `₹${c.spend} (${S.mock})`} testid={`compare-spend-${c.id}`} />
    </div>
  );
}

export function CampaignCompare({ ads }) {
  const A = useAdStrings();
  const S = { ...A.cmp, na: A.L.na };
  const l = A.locale;
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState({ a: "", b: "" });
  const [res, setRes] = useState(null);
  const [state, setState] = useState("");
  const run = async () => {
    setState("loading"); setRes(null);
    try { setRes(await compareAds(sel.a, sel.b)); setState(""); }
    catch (e) { setState(e?.response?.status === 400 ? S.same : S.err); }
  };
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50 px-3 py-1.5 text-xs font-semibold" data-testid="compare-open-btn">
      <GitCompare className="w-3.5 h-3.5" /> {S.open}
    </button>
  );
  const pick = (k) => (
    <select value={sel[k]} onChange={(e) => setSel((s) => ({ ...s, [k]: e.target.value }))} className="w-full min-w-0 border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white" data-testid={`compare-select-${k}`}>
      <option value="">{S.choose} {k.toUpperCase()}</option>
      {ads.filter((a) => a.id !== sel[k === "a" ? "b" : "a"]).map((a) => <option key={a.id} value={a.id}>{label(a)}</option>)}
    </select>
  );
  return (
    <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-3" data-testid="compare-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold inline-flex items-center gap-1"><GitCompare className="w-3.5 h-3.5 text-emerald-700" /> {S.title}</div>
        <button type="button" onClick={() => { setOpen(false); setRes(null); }} className="text-[11px] text-slate-500 hover:underline" data-testid="compare-close-btn">{S.close}</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
        {pick("a")}{pick("b")}
        <button type="button" onClick={run} disabled={!sel.a || !sel.b || state === "loading"} className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-1.5 text-xs font-semibold disabled:opacity-50" data-testid="compare-run-btn">
          {state === "loading" ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : S.run}
        </button>
      </div>
      {state && state !== "loading" && <div className="text-[11px] text-rose-600 mt-2" data-testid="compare-error">{state}</div>}
      {res && (
        <>
          <div className="grid grid-cols-2 gap-2 mt-3" data-testid="compare-result">{res.campaigns.map((c) => <CompareCard key={c.id} c={c} S={S} l={l} />)}</div>
          <div className="text-[10px] text-slate-400 mt-1.5">{S.foot}</div>
        </>
      )}
    </div>
  );
}
